import React, { useState } from 'react';
import { ChevronDown, Check, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';

// Any option whose value matches this string (case-insensitive) is treated
// as a sentinel that opens an inline write-in input instead of being added
// to the selection. Settings admins add this option to a dropdown's option
// list to enable per-report free-form entries without changing the global
// option set. Saved typed values land on the report only.
const TYPE_ANOTHER_SENTINEL = 'type another option';
const isTypeAnotherOption = (v) =>
  typeof v === 'string' && v.trim().toLowerCase() === TYPE_ANOTHER_SENTINEL;

/**
 * Multi-select dropdown used across field forms.
 *
 * Drop-in replacement for the older CheckboxGroup component:
 *   - Visually identical to a single <Select> dropdown trigger
 *   - Opens a popover with checkable options inside — supports multi-select
 *   - State shape stays the same as CheckboxGroup (string[] | string | null)
 *   - Calls `onChange(arr: string[])` so callers don't need to change
 *
 * Why a popover-with-checkboxes instead of a real <select multiple>?
 *   - Native multi-select is awful on touch devices (Ctrl/Cmd-click only),
 *     and field workers are largely on phones/tablets
 *   - We need to render colored swatches & "removed" tags for legacy options,
 *     which a native <select> can't do
 *
 * Props mirror CheckboxGroup so swapping is mechanical:
 *   options:    [{ value, label, color?, legacy? }] OR ["string", ...]
 *   value:      string | string[] | null
 *   onChange:   (next: string[]) => void
 *   disabled:   boolean
 *   placeholder: string shown when nothing is selected (default "Select...")
 *   testIdBase: optional prefix for data-testid on each row
 *   renderOption: optional (option, isChecked) => ReactNode for custom item UI
 *   allowCustom: boolean — when true, render a write-in input at the bottom
 *                of the popover so users can add free-form values that
 *                aren't in the seeded option list (used for "Other" cases
 *                on field-report forms).
 */
export default function MultiSelectDropdown({
  options = [],
  value,
  onChange,
  disabled = false,
  placeholder = 'Select...',
  testIdBase,
  renderOption,
  className = '',
  allowCustom = false,
}) {
  const [open, setOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState('');
  // Toggled true when the user clicks the "Type another option" sentinel
  // row. Reveals an inline write-in input so they can add a free-form value
  // for this report. Independent of the static `allowCustom` prop.
  const [showWriteIn, setShowWriteIn] = useState(false);

  // Accept legacy string, array, or null/undefined.
  const selected = Array.isArray(value)
    ? value
    : value
      ? [String(value)]
      : [];

  // The sentinel itself must never appear in the saved value — strip any
  // accidental occurrence (e.g. legacy reports) from the visible selection.
  const visibleSelected = selected.filter((s) => !isTypeAnotherOption(s));

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  );

  const toggle = (v) => {
    if (disabled) return;
    // Clicking the "Type another option" row opens the write-in input
    // instead of toggling a real selection.
    if (isTypeAnotherOption(v)) {
      setShowWriteIn(true);
      return;
    }
    const isOn = selected.includes(v);
    const next = isOn ? selected.filter((s) => s !== v) : [...selected, v];
    onChange(next);
  };

  const commitCustom = () => {
    const v = customDraft.trim();
    if (!v) return;
    if (isTypeAnotherOption(v)) {
      // Don't let users type the sentinel itself.
      setCustomDraft('');
      return;
    }
    if (!selected.includes(v)) onChange([...selected, v]);
    setCustomDraft('');
  };

  // Build the visible summary inside the trigger. We render text only — chip
  // wrapping inside the 36px trigger height looks terrible on mobile.
  const triggerLabel = visibleSelected.length === 0
    ? placeholder
    : visibleSelected.join(', ');

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          data-testid={testIdBase ? `${testIdBase}-trigger` : undefined}
          className={cn(
            // Mirror SelectTrigger styling so the field doesn't look out of place.
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden',
            visibleSelected.length === 0 && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate text-left flex-1">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-1 w-[--radix-popover-trigger-width] min-w-[14rem] max-h-72 overflow-y-auto"
      >
        {normalizedOptions.length === 0 && (
          <div className="px-2 py-3 text-sm text-muted-foreground text-center">
            No options
          </div>
        )}
        {normalizedOptions.map((opt) => {
          const isChecked = selected.includes(opt.value);
          const isSentinel = isTypeAnotherOption(opt.value);
          const testId = testIdBase
            ? `${testIdBase}-${String(opt.value).toLowerCase().replace(/\s+/g, '-')}`
            : undefined;
          if (isSentinel) {
            // Render as an "action row" — no checkbox, plus icon, clicking
            // it opens the inline write-in input below.
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                data-testid={testIdBase ? `${testIdBase}-type-another` : testId}
                className="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left text-amber-700 hover:bg-amber-50 focus:bg-amber-50 focus:outline-none border-t border-slate-200 mt-1 pt-2"
              >
                <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="flex-1 truncate italic">{opt.label || opt.value}</span>
              </button>
            );
          }
          return (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={isChecked}
              onClick={() => toggle(opt.value)}
              data-testid={testId}
              className={cn(
                'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:outline-none',
                isChecked && 'bg-accent/50',
              )}
            >
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-sm border border-primary flex-shrink-0',
                  isChecked
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-transparent',
                )}
              >
                {isChecked && <Check className="h-3 w-3" />}
              </span>
              {renderOption ? (
                renderOption(opt, isChecked)
              ) : (
                <span className="flex-1 truncate">
                  {opt.label || opt.value}
                  {opt.legacy && (
                    <span
                      className="ml-1.5 inline-block align-middle text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0 rounded"
                      title="This option was removed from settings — it remains here because it was selected on a previous version of this report."
                    >
                      removed
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
        {(allowCustom || showWriteIn) && !disabled && (
          <div className="border-t border-slate-200 mt-1 pt-2 px-2 pb-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
              Other — type your own
            </div>
            <div className="flex gap-1">
              <input
                type="text"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitCustom();
                  }
                }}
                placeholder="Add custom value…"
                autoFocus={showWriteIn}
                data-testid={testIdBase ? `${testIdBase}-custom-input` : undefined}
                className="flex-1 h-7 text-xs px-2 rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
              <button
                type="button"
                onClick={commitCustom}
                data-testid={testIdBase ? `${testIdBase}-custom-add` : undefined}
                className="text-xs px-2 h-7 rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40"
                disabled={!customDraft.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
